/**
 * PostReviewSurvey — ⭐ 1-5 micro-survey after submission review completion
 *
 * Shows a tiny overlay dialog after a teacher finishes reviewing a submission
 * (triggered externally via the `open` prop).
 * Q1: 1-5 rating. Q2 (conditional, ≤3 stars): "最大的問題是甚麼？" + preset chips.
 * Auto-dismisses after 10 seconds if no interaction.
 * Gated behind VITE_FEEDBACK_MODE env var — renders nothing when off.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Stack,
  Typography,
  IconButton,
  Rating,
  Chip,
  TextField,
  Button,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { supabase } from '@/lib/supabase';

/** Q2 follow-up chips (only shown when rating ≤ 3) */
const FOLLOW_UP_CHIPS = [
  { label: '評分不準', value: 'wrong_grade' },
  { label: '流程太慢', value: 'too_slow' },
  { label: '回饋不夠具體', value: 'feedback_vague' },
  { label: '介面難用', value: 'ui_hard' },
] as const;

interface PostReviewSurveyProps {
  /** Whether to show the survey */
  open: boolean;
  /** Called when the survey should close (submitted, dismissed, or auto-dismissed) */
  onClose: () => void;
  submissionId: string;
  assignmentId: string;
  totalQuestions: number;
  totalFlagged: number;
}

export function PostReviewSurvey({
  open: openProp,
  onClose,
  submissionId,
  assignmentId,
  totalQuestions,
  totalFlagged,
}: PostReviewSurveyProps) {
  // Gate: render nothing when feedback mode is off
  if (import.meta.env.VITE_FEEDBACK_MODE !== 'true') return null;
  return (
    <PostReviewSurveyInner
      open={openProp}
      onClose={onClose}
      submissionId={submissionId}
      assignmentId={assignmentId}
      totalQuestions={totalQuestions}
      totalFlagged={totalFlagged}
    />
  );
}

function PostReviewSurveyInner({
  open: openProp,
  onClose,
  submissionId,
  assignmentId,
  totalQuestions,
  totalFlagged,
}: PostReviewSurveyProps) {
  const [rating, setRating] = useState<number | null>(null);
  const [selectedChips, setSelectedChips] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const autoDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss after 10s if no interaction
  useEffect(() => {
    if (!openProp) {
      // Reset state when closed
      setRating(null);
      setSelectedChips([]);
      setComment('');
      setInteracted(false);
      setSubmitting(false);
      if (autoDismissTimer.current) clearTimeout(autoDismissTimer.current);
      return;
    }

    autoDismissTimer.current = setTimeout(() => {
      if (!interacted) {
        onClose();
      }
    }, 10_000);

    return () => {
      if (autoDismissTimer.current) clearTimeout(autoDismissTimer.current);
    };
  }, [openProp, interacted, onClose]);

  const handleInteraction = useCallback(() => {
    setInteracted(true);
    if (autoDismissTimer.current) {
      clearTimeout(autoDismissTimer.current);
      autoDismissTimer.current = null;
    }
  }, []);

  const handleToggleChip = useCallback((chip: string) => {
    setSelectedChips((prev) =>
      prev.includes(chip) ? prev.filter((c) => c !== chip) : [...prev, chip],
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    if (rating === null) return;
    setSubmitting(true);
    handleInteraction();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        console.error('[PostReviewSurvey] No auth token');
        setSubmitting(false);
        return;
      }

      // Capture screenshot
      let screenshotData: string | null = null;
      try {
        const h2c = (await import('html2canvas')).default;
        const canvas = await h2c(document.body, {
          logging: false,
          useCORS: true,
          allowTaint: true,
          scale: 2,
        });
        screenshotData = canvas.toDataURL('image/jpeg', 0.85);
      } catch {
        // Non-critical
      }

      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          source: 'post_review',
          sentiment: null,
          rating,
          tags: selectedChips,
          comment: comment.trim() || null,
          page_route: window.location.pathname + window.location.search,
          assignment_id: assignmentId,
          submission_id: submissionId,
          screenshot_data: screenshotData,
        }),
      });

      if (!res.ok) {
        console.error('[PostReviewSurvey] Submit failed:', await res.text());
      }
    } catch (err) {
      console.error('[PostReviewSurvey] Error:', err);
    } finally {
      setSubmitting(false);
      onClose();
    }
  }, [rating, selectedChips, comment, assignmentId, submissionId, onClose, handleInteraction]);

  const showFollowUp = rating !== null && rating <= 3;

  return (
    <Dialog
      open={openProp}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            position: 'fixed',
            bottom: 80,
            right: 24,
            m: 0,
            width: 300,
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          {/* Close button */}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ position: 'absolute', top: 4, right: 4 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Q1: Rating */}
          <Typography variant="subtitle2" fontWeight={700}>
            你覺得這次 AI 批改的整體表現如何？
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Rating
              value={rating}
              onChange={(_, newValue) => {
                handleInteraction();
                setRating(newValue);
              }}
              icon={<StarIcon fontSize="large" />}
              size="large"
              sx={{
                '& .MuiRating-iconFilled': { color: 'warning.main' },
              }}
            />
          </Box>

          {/* Q2: Follow-up (only if rating ≤ 3) */}
          {showFollowUp && (
            <Box>
              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                最大的問題是甚麼？
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {FOLLOW_UP_CHIPS.map((chip) => (
                  <Chip
                    key={chip.value}
                    label={chip.label}
                    size="small"
                    variant={selectedChips.includes(chip.value) ? 'filled' : 'outlined'}
                    color={selectedChips.includes(chip.value) ? 'warning' : 'default'}
                    onClick={() => handleToggleChip(chip.value)}
                    sx={{ fontSize: '0.7rem', height: 24 }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Optional comment */}
          {showFollowUp && (
            <TextField
              placeholder="補充說明（選填）"
              multiline
              minRows={1}
              maxRows={2}
              fullWidth
              size="small"
              value={comment}
              onChange={(e) => {
                handleInteraction();
                setComment(e.target.value);
              }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
            />
          )}

          {/* Submit */}
          <Button
            variant="contained"
            size="small"
            disabled={submitting || rating === null}
            onClick={handleSubmit}
            fullWidth
          >
            送出
          </Button>

          <Typography variant="caption" color="text.secondary" textAlign="center">
            此問卷僅供內部評估用途
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
